/**
 * Configuración central de idiomas.
 *
 * Distinguimos dos conceptos que antes estaban mezclados en un solo valor:
 *
 * - ELECCIÓN (`LanguageChoice`): lo que el usuario elige en el selector, antes
 *   de loguearse. El español es genérico (`es`): no se le pide que sepa si su
 *   colegio es de España o de LatAm.
 * - LOCALE RESUELTO (`AppLocale`): el diccionario concreto que carga
 *   ngx-translate. Para `es` se resuelve recién al iniciar sesión, según el
 *   país del colegio del usuario.
 */

/**
 * Opciones del selector de idioma (pre y post login).
 *
 * Son códigos de FAMILIA, no locales: ninguno se puede usar directo como
 * nombre de diccionario. El backend los resuelve al locale concreto según el
 * país del colegio (ver `LanguageVariants`).
 */
export type LanguageChoice = 'es' | 'en' | 'ca';

/** Diccionarios reales bajo `assets/i18n/`. */
export type AppLocale = 'es-ES' | 'es-LA' | 'en-US' | 'ca';

export const LANGUAGE_CHOICES: readonly { code: LanguageChoice; label: string }[] = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'ca', label: 'Català' },
];

/**
 * Locales de cada familia. El PRIMERO es el default: el que se usa antes de
 * loguearse y cuando el backend no resuelve nada.
 *
 * Es la única tabla que hay que tocar para sumar una variante: agregar
 * `en-GB` acá (y su `en-GB.json`) alcanza para que el backend pueda
 * resolverla.
 */
export const CHOICE_LOCALES: Readonly<Record<LanguageChoice, readonly AppLocale[]>> = {
  es: ['es-ES', 'es-LA'],
  en: ['en-US'],
  ca: ['ca'],
};

/** Locale con el que se renderiza todo lo que no tiene variante regional. */
export const FALLBACK_LOCALE: AppLocale = 'es-ES';

/** Elección por defecto cuando el usuario nunca eligió nada. */
export const DEFAULT_CHOICE: LanguageChoice = 'es';

/** Todos los diccionarios que hay que registrar en ngx-translate. */
export const SUPPORTED_LOCALES: readonly AppLocale[] = ['es-ES', 'es-LA', 'en-US', 'ca'];

/** Locale a usar para una elección cuando nadie resolvió una variante. */
export function defaultLocaleForChoice(choice: LanguageChoice): AppLocale {
  return CHOICE_LOCALES[choice][0];
}

/** True si el locale pertenece a la familia de esa elección. */
export function localeBelongsToChoice(locale: string, choice: LanguageChoice): boolean {
  return CHOICE_LOCALES[choice].includes(locale as AppLocale);
}

/**
 * Elección a la que pertenece un locale, o `null` si no es ninguno conocido.
 *
 * Se usa para migrar: los usuarios que ya estaban en la plataforma tienen el
 * locale viejo guardado pero no tienen elección, porque la separación entre
 * ambas cosas es posterior.
 */
export function choiceForLocale(locale: string | null | undefined): LanguageChoice | null {
  if (!locale) {
    return null;
  }
  const entry = (Object.keys(CHOICE_LOCALES) as LanguageChoice[]).find((choice) =>
    localeBelongsToChoice(locale, choice),
  );
  return entry ?? null;
}

export const SPAIN_LOCALE: AppLocale = 'es-ES';
export const LATAM_LOCALE: AppLocale = 'es-LA';

/** Clave de localStorage: la elección del usuario. Sobrevive al logout. */
export const CHOICE_STORAGE_KEY = 'language_choice';

/**
 * Clave de localStorage: el locale resuelto. Lo consumen ngx-translate y —
 * leyéndolo directo — `pdf-factory.service.ts` y `sociogram-pdf.service.ts`.
 * Se resetea en el logout para no arrastrar el país de un usuario al siguiente.
 */
export const LOCALE_STORAGE_KEY = 'language';

/**
 * Mapa que el backend manda en cada `users_organizations[]`: la elección del
 * usuario → el locale concreto a cargar.
 *
 *   { "es": "es-LA", "en": "en-US", "ca": "ca",
 *     "es-ES": "es-ES", "es-LA": "es-LA", "en-US": "en-US" }
 *
 * Las claves de familia (`es`, `en`) las resuelve el backend con el país del
 * colegio; las de código concreto (`es-ES`, `en-US`, `ca`) se devuelven tal
 * cual, porque si el usuario pidió una variante explícita el país no debe
 * sobreescribirla.
 *
 * Antes el front tenía la lista de países hardcodeada. Tenía dos errores
 * —faltaba Ecuador, que sí tiene colegio activo, y sobraba Bolivia, que no
 * existe como país en la base— y agregar un país obligaba a tocar el front.
 */
export type LanguageVariants = Readonly<Record<string, string>>;

/**
 * Locale a cargar para la elección del usuario, según el mapa del backend.
 *
 * Si el mapa no viene, o no trae la clave, se usa el default de esa familia
 * (`CHOICE_LOCALES`). Nunca se cae a es-ES para todas las elecciones: eso le
 * cambiaría la interfaz a español a alguien que eligió inglés.
 */
export function resolveLocaleFromVariants(
  variants: LanguageVariants | null | undefined,
  choice: LanguageChoice,
): AppLocale {
  const fallback = defaultLocaleForChoice(choice);
  const resolved = variants?.[choice];

  if (!resolved) {
    return fallback;
  }
  if (!SUPPORTED_LOCALES.includes(resolved as AppLocale)) {
    // El backend resolvió a un locale para el que no tenemos diccionario (p. ej.
    // en-GB antes de crear en-GB.json). Cargarlo daría 404, así que se usa el
    // fallback — pero se avisa, porque si no es una falla silenciosa.
    console.warn(
      `[i18n] el backend resolvió "${choice}" a "${resolved}", que no tiene ` +
        `diccionario en assets/i18n/. Se usa "${fallback}".`,
    );
    return fallback;
  }
  return resolved as AppLocale;
}

// `LOCALES_WITH_REPORT_ASSETS` y `resolveReportAssetLocale()` vivían acá para
// resolver a qué carpeta de `assets/images/reports/` apuntar según el idioma.
// Se eliminaron junto con esos assets: la generación del reporte pasó al
// backend —como anticipaba el comentario que tenían— y el front ya solo baja
// el PDF armado (ver `PdfService.downloadReport`).

/**
 * Locale válido para las APIs de `Intl` (fechas, números).
 *
 * `es-LA` es un código interno acordado con backend, NO es BCP-47 válido: ahí
 * `LA` es la región de Laos. `Intl` no tira error — silenciosamente formatea
 * mal. El código estándar para español latinoamericano es `es-419`.
 */
export function resolveIntlLocale(locale: string | null | undefined): string {
  if (!locale) return SPAIN_LOCALE;
  return locale === LATAM_LOCALE ? 'es-419' : locale;
}
